import log from 'loglevel';
import Vuex from 'vuex';

import empty from './empty';
import express from './express';

import { generateProject, uploadRecipe } from '../utils/api-client';

const SUPPORTED_PLATFORMS = {
  express,
  empty,
};

const store = new Vuex.Store({
  state: {
    recipe: {
      platform: null,
      name: '',
      description: '',
    },
    currentStep: 'project',
    error: null,
    serverOutput: null,
    serverError: null,
    generatedUrl: null,
  },

  getters: {
    enumeratedSteps(state, getters) {
      const retval = [];
      for (const s of getters.navigationSteps) {
        retval.push(s.key);
        if (s.steps) {
          for (const sub of s.steps) {
            retval.push(sub.key);
          }
        }
      }
      return retval;
    },

    canNavigateAway(state) {
      return !state.error && !state.generatedUrl;
    },

  },

  mutations: {
    /**
     * Go to a different step in the wizard.
     *
     * @param {string} where - Where to go.
     *
     * The `where` parameter can either be a special string `prev` or
     * `next` or a step key.
     *
     * If the special prev/next string is used, mutation will change
     * the current step to the previous or next *top-level* step.
     * If no top-level step exists before or after the current
     * step, the mutation does nothing.
     */
    navigate(state, where) {
      const steps = this.getters.enumeratedSteps;
      const topSteps = this.getters.navigationSteps.map((s) => s.key);

      if (state.error) {
        log.error('store.navigate(): error in current step. refusing to navigate away');
        return;
      }

      if (steps.includes(where)) {
        log.debug(`store.navigate(): navigating directly to step ${where}`);
        state.currentStep = where;
        return;
      }

      /* We must check for this *after* we check for direct navigation. If we're
       * renaming the model, the new navigation target (models/NewModelName) will
       * exist but old (models/OldModelName) won't, so we don't want to crash & burn
       * in that case.
       */
      let idx = steps.indexOf(state.currentStep);
      if (idx === -1) {
        log.error(`store.navigate(): current step '${state.currentStep}' not in all steps: ${steps.join(',')}`);
        return;
      }

      if (where === 'prev') {
        while (idx > 0) {
          idx -= 1;
          if (topSteps.includes(steps[idx])) {
            state.currentStep = steps[idx];
            log.debug(`store.navigate(): navigating to previous top-level step (${state.currentStep})`);
            return;
          }
        }
        log.error(`store.navigate(): no top-level steps before ${state.currentStep}, not going anywhere`);
      } else if (where === 'next') {
        while (idx + 1 < steps.length) {
          idx += 1;
          if (topSteps.includes(steps[idx])) {
            state.currentStep = steps[idx];
            log.debug(`store.navigate(): navigating to next step (${state.currentStep})`);
            return;
          }
        }
        log.error(`store.navigate(): no top-level steps after ${state.currentStep}, not going anywhere`);
      } else {
        log.error(`store.navigate(): invalid direction option ${where}`);
      }
    },

    /**
     * Enable store logic for selected platform. If any other
     * platform was previously enabled, disable it.
     *
     * @param {string} platform - one of SUPPORTED_PLATFORMS
     *
     * If recipe is empty, the "empty" platform store can be used.
     */
    setPlatformSupport(state, platform) {
      if (this.hasModule(platform)) return;

      for (const m of Object.keys(SUPPORTED_PLATFORMS)) {
        if (this.hasModule(m)) {
          log.debug(`store.setRecipe(): disabling platform '${m}'`);
          this.unregisterModule(m);
        }
      }
      log.debug(`store.setRecipe(): enabling platform '${platform}'`);
      this.registerModule(platform, SUPPORTED_PLATFORMS[platform]);
    },

    /**
     * Use the given object as the recipe
     *
     * @param {object} recipe - The new recipe
     *
     * Setting entire recipe at once is useful when initializing store
     * with existing recipe, such as the one loaded from the server.
     *
     * This checks that the recipe uses one of the supported platforms and
     * changes the current platform to it (see `setPlatformSupport`). If
     * recipe platform is unknown, the recipe is rejected.
     */
    setRecipe(state, recipe) {
      const { platform } = recipe;
      if (!platform) {
        log.error('store.setRecipe(): rejecting recipe without a platform');
        return;
      }

      const supportedList = Object.keys(SUPPORTED_PLATFORMS).filter((p) => p !== 'empty');

      if (!supportedList.includes(platform)) {
        log.error(`store.setRecipe(): rejecting recipe for unsupported platform ${platform} (supported: ${supportedList.join(',')})`);
        return;
      }

      this.commit('setPlatformSupport', platform);

      log.info(`store.setRecipe(): setting recipe for platform ${platform}`);
      state.recipe = recipe;
    },

    /**
     * Update recipe with given data
     *
     * @param {object} data - Recipe data to update
     *
     * The recipe properties will be overwritten with the properties
     * contained in tha passed-in update data. Note that no deep merge
     * is done - top-level properties are overwritten directly.
     */
    updateRecipe(state, updateData) {
      log.debug(`store.updateRecipe(): updating recipe with: ${JSON.stringify(updateData)}`);
      state.recipe = { ...state.recipe, ...updateData };
    },

    /**
     * Set error flag
     *
     * @param error - Error details
     *
     * Flag is used to prevent navigating away from the current step. The error
     * data itself is currently not used.
     */
    setError(state, error) {
      state.error = error;
    },

    /**
     * Clear error flag
     *
     * Clear error flag, allowing navigation to resume (see `setError`).
     */
    clearError(state) {
      state.error = null;
    },

    setServerOutput(state, output) {
      state.serverOutput = output;
      state.generatedUrl = null;
      state.serverError = null;
    },

    setServerError(state, err) {
      state.generatedUrl = null;
      state.serverOutput = null;
      state.serverError = err;
    },

    setGeneratedUrl(state, url) {
      state.serverError = null;
      state.generatedUrl = url;
    },
  },

  actions: {
    async uploadRecipe({ commit, rootState }, newRecipe) {
      try {
        const output = await uploadRecipe(newRecipe || rootState.recipe);
        commit('setServerOutput', output);
        if (newRecipe) commit('setRecipe', newRecipe);
      } catch (err) {
        commit('setServerError', err);
      }
    },

    async generateProject({ commit }, description) {
      try {
        const result = await generateProject(description);
        // result=null w/o error means we're in dev mode without backend
        // we still want to set URL to something so nav is disabled properly
        commit('setGeneratedUrl', result ? result.archive_url : '#');
      } catch (err) {
        log.error('generateProject(): error generating project:', err);
        commit('setServerError', err);
      }
    }
  },
});

store.registerModule('empty', empty);

export default store;
